// ---------------------------------------------------------------------------
// xurl credential writer
//
// Builds and merges the YAML store at ~/.xurl that the official xurl CLI
// (https://github.com/xdevplatform/xurl) reads at startup. The on-disk schema
// is mirrored from xurl's `store/tokens.go`:
//
//   apps:
//     <app-name>:
//       client_id: "..."
//       client_secret: "..."
//       default_user: ""
//       oauth2_tokens:
//         <username>:
//           type: oauth2
//           oauth2: { access_token, refresh_token, expiration_time }
//       oauth1_token:
//         type: oauth1
//         oauth1: { access_token, token_secret, consumer_key, consumer_secret }
//       bearer_token:
//         type: bearer
//         bearer: "..."
//   default_app: <app-name>
//
// agt writes one app per xurl integration, prefixed `agt-`. Apps the user
// configured manually with `xurl auth apps add` are preserved untouched.
// ---------------------------------------------------------------------------

import { chmodSync, existsSync, mkdirSync, readFileSync, renameSync, unlinkSync, writeFileSync } from 'node:fs';
import { homedir } from 'node:os';
import { dirname, join } from 'node:path';
import { parse as parseYaml, stringify as stringifyYaml } from 'yaml';
import type { ResolvedIntegration } from '../types/integration.js';

const XURL_FILE_MODE = 0o600;

export interface XurlOAuth1Payload {
  access_token: string;
  token_secret: string;
  consumer_key: string;
  consumer_secret: string;
}

export interface XurlOAuth2Payload {
  access_token: string;
  refresh_token: string;
  expiration_time: number;
}

export interface XurlToken {
  type: 'bearer' | 'oauth2' | 'oauth1';
  bearer?: string;
  oauth2?: XurlOAuth2Payload;
  oauth1?: XurlOAuth1Payload;
}

export interface XurlApp {
  client_id: string;
  client_secret: string;
  default_user?: string;
  oauth2_tokens?: Record<string, XurlToken>;
  oauth1_token?: XurlToken;
  bearer_token?: XurlToken;
}

export interface XurlStore {
  apps: Record<string, XurlApp>;
  default_app?: string;
}

/** Prefix for apps written by the agt manager — distinguishes them from
 * user-managed apps so cleanup/upgrade logic can act safely. */
export const XURL_AGT_APP_PREFIX = 'agt-';

function asString(v: unknown): string | undefined {
  return typeof v === 'string' && v.length > 0 ? v : undefined;
}

/**
 * Returns the canonical xurl app name for an integration.
 *
 * Defaults to `agt-xurl`. When `config.x_username` is supplied, the username
 * is appended (`agt-xurl-<username>`) so multiple xurl integrations targeting
 * different X accounts land in distinct `apps[...]` entries instead of the
 * last-write-wins collapse that `definition_id` alone would cause.
 */
export function xurlAppNameFor(integration: ResolvedIntegration): string {
  const username = asString(integration.config?.['x_username']);
  const base = `${XURL_AGT_APP_PREFIX}${integration.definition_id}`;
  return username ? `${base}-${username.toLowerCase()}` : base;
}

/**
 * Build an xurl App entry from a ResolvedIntegration.
 *
 * Returns `null` when the integration has no usable credentials. Otherwise
 * fills bearer_token (api_key auth), oauth2_tokens (oauth2 auth), and
 * oauth1_token (when all four oauth1_* config fields are present).
 */
export function buildXurlAppFromIntegration(integration: ResolvedIntegration): XurlApp | null {
  const cfg = integration.config ?? {};
  const creds = integration.credentials ?? {};

  const app: XurlApp = {
    client_id: asString(cfg['client_id']) ?? '',
    client_secret: asString(cfg['client_secret']) ?? '',
  };

  const apiKey = asString(creds['api_key']);
  if (integration.auth_type === 'api_key' && apiKey) {
    app.bearer_token = { type: 'bearer', bearer: apiKey };
  }

  // ENG-7506: `github_app` is normalized to the oauth2 credential shape
  // server-side (host-runtime mints a short-lived installation token into
  // `credentials.access_token`), so it authenticates xurl as a bearer/oauth2
  // token identically to oauth2.
  const accessToken = asString(creds['access_token']);
  if (
    (integration.auth_type === 'oauth2' || integration.auth_type === 'github_app') &&
    accessToken
  ) {
    const username = asString(cfg['x_username']) ?? 'default';
    const expiresAt = asString(creds['token_expires_at']);
    const expirationTime = expiresAt ? Math.floor(Date.parse(expiresAt) / 1000) : 0;
    app.oauth2_tokens = {
      [username]: {
        type: 'oauth2',
        oauth2: {
          access_token: accessToken,
          refresh_token: asString(creds['refresh_token']) ?? '',
          expiration_time: Number.isFinite(expirationTime) ? expirationTime : 0,
        },
      },
    };
    app.default_user = username;
  }

  const ck = asString(cfg['oauth1_consumer_key']);
  const cs = asString(cfg['oauth1_consumer_secret']);
  const at = asString(cfg['oauth1_access_token']);
  const ts = asString(cfg['oauth1_token_secret']);
  if (ck && cs && at && ts) {
    app.oauth1_token = {
      type: 'oauth1',
      oauth1: { access_token: at, token_secret: ts, consumer_key: ck, consumer_secret: cs },
    };
  }

  if (!app.bearer_token && !app.oauth2_tokens && !app.oauth1_token) {
    return null;
  }
  return app;
}

/**
 * Merge agt-managed xurl apps into an existing store, preserving every
 * non-agt-prefixed app the user added manually with `xurl auth apps add`.
 *
 * Critically, existing `agt-*` entries that are NOT present in the new
 * `agtApps` map are dropped — the current set of integrations is the
 * source of truth, so stale tokens for removed integrations must not
 * linger on disk.
 *
 * `default_app` is left untouched if it still resolves to an app that
 * exists post-merge; otherwise it falls back to the first agt-managed
 * app so the CLI has a sensible default.
 */
export function mergeXurlStore(existing: XurlStore | null, agtApps: Record<string, XurlApp>): XurlStore {
  const apps: Record<string, XurlApp> = {};

  // 1. Keep every user-managed (non-agt-prefixed) app from the existing store.
  for (const [name, app] of Object.entries(existing?.apps ?? {})) {
    if (!name.startsWith(XURL_AGT_APP_PREFIX)) apps[name] = app;
  }
  // 2. Overlay the current set of agt-managed apps. Any previous agt-* entry
  //    that isn't in `agtApps` is intentionally dropped here — stale creds.
  for (const [name, app] of Object.entries(agtApps)) {
    apps[name] = app;
  }

  let default_app = existing?.default_app;
  if (default_app && !apps[default_app]) {
    // The previous default_app pointed at an app that no longer exists
    // (e.g., the user deleted it, or an agt app was removed).
    default_app = undefined;
  }
  if (!default_app) {
    default_app = Object.keys(apps).find((n) => n.startsWith(XURL_AGT_APP_PREFIX));
  }

  const result: XurlStore = { apps };
  if (default_app) result.default_app = default_app;
  return result;
}

/**
 * Parse an existing ~/.xurl YAML payload, tolerant of empty input but
 * strict about structure — a parsed YAML object only qualifies as a
 * XurlStore when it actually carries xurl-specific fields (`apps` and/or
 * `default_app`). This prevents unrelated YAML (e.g. `foo: bar`) from
 * being treated as a valid empty store and then overwritten.
 */
export function parseXurlStore(yaml: string | null | undefined): XurlStore | null {
  if (!yaml) return null;
  try {
    const parsed = parseYaml(yaml) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;

    const obj = parsed as Record<string, unknown>;
    const hasApps = 'apps' in obj;
    const hasDefault = 'default_app' in obj;

    // Neither xurl-specific field present → treat as foreign YAML, fail closed.
    if (!hasApps && !hasDefault) return null;

    // When `apps` is present it must be a plain object (not null, not array).
    if (hasApps && obj['apps'] != null && (typeof obj['apps'] !== 'object' || Array.isArray(obj['apps']))) {
      return null;
    }
    if (hasDefault && obj['default_app'] != null && typeof obj['default_app'] !== 'string') {
      return null;
    }

    return {
      apps: (obj['apps'] as Record<string, XurlApp> | undefined) ?? {},
      ...(typeof obj['default_app'] === 'string' && obj['default_app'] ? { default_app: obj['default_app'] } : {}),
    };
  } catch {
    return null;
  }
}

/** Serialize a store to YAML — suitable for writing to ~/.xurl. */
export function serializeXurlStore(store: XurlStore): string {
  return stringifyYaml(store);
}

/**
 * Build the agt-managed apps map for a set of resolved integrations.
 * Filters out non-xurl integrations and integrations with no usable credentials.
 */
export function buildAgtXurlApps(integrations: ResolvedIntegration[]): Record<string, XurlApp> {
  const result: Record<string, XurlApp> = {};
  for (const integration of integrations) {
    if (integration.definition_id !== 'xurl') continue;
    const app = buildXurlAppFromIntegration(integration);
    if (app) {
      result[xurlAppNameFor(integration)] = app;
    }
  }
  return result;
}

/**
 * Resolve the on-disk path to the xurl store. Mirrors xurl's own logic
 * (`os.UserHomeDir() / .xurl`) and respects $HOME / $USERPROFILE so tests
 * can target a temp dir.
 */
export function getXurlStorePath(): string {
  const home = process.env['HOME'] ?? process.env['USERPROFILE'] ?? homedir();
  return join(home, '.xurl');
}

/**
 * Persist agt-managed xurl apps into the user's `~/.xurl`, merging with any
 * apps the user added manually with `xurl auth apps add`. No-ops when the
 * caller provides no xurl integrations with usable credentials, so calling
 * it on every refresh is safe.
 *
 * Returns the absolute path written to, or `null` if nothing was written.
 */
export function writeXurlStoreForIntegrations(
  integrations: ResolvedIntegration[],
  filePath: string = getXurlStorePath(),
): string | null {
  const agtApps = buildAgtXurlApps(integrations);
  if (Object.keys(agtApps).length === 0) return null;

  let existing: XurlStore | null = null;
  if (existsSync(filePath)) {
    let raw: string;
    try {
      raw = readFileSync(filePath, 'utf-8');
    } catch {
      // Fail closed: an unreadable existing file must not be clobbered,
      // or we could wipe out user-managed apps.
      return null;
    }
    const parsed = parseXurlStore(raw);
    if (!parsed && raw.trim().length > 0) {
      // Fail closed: the file has content but parseXurlStore could not
      // recognise it as a valid xurl store. Proceeding would overwrite
      // user-managed apps, so abort the merge.
      return null;
    }
    existing = parsed;
  }

  const merged = mergeXurlStore(existing, agtApps);

  mkdirSync(dirname(filePath), { recursive: true });

  // Atomic write: stage to a sibling temp file with the restrictive mode
  // applied from creation, then rename over the target so an interrupted
  // process can never leave ~/.xurl partially written or corrupted.
  const tmpPath = `${filePath}.tmp-${process.pid}-${Date.now()}`;
  writeFileSync(tmpPath, serializeXurlStore(merged), { mode: XURL_FILE_MODE });
  try {
    renameSync(tmpPath, filePath);
  } catch (err) {
    try { unlinkSync(tmpPath); } catch { /* ignore */ }
    throw err;
  }
  try {
    chmodSync(filePath, XURL_FILE_MODE);
  } catch {
    // Best-effort: chmod fails on some platforms (e.g. Windows). The
    // temp file was created with the restrictive mode, so the renamed
    // target should already have it.
  }

  return filePath;
}
